import { white } from '@kmns-internal/furikake/tokens'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { Box, Stack, TableCell } from '@mui/material'
import { Image } from '@ulysses-inc/harami_api_client'
import { FC, useMemo } from 'react'
import { bodyCellSx } from './styles'
import { TableSectionRecordedAt } from './TableSectionRecordedAt'

type Props = {
  images: Image[]
  recordedAt?: Date
  goPreviewPhoto: (imageUrls: string[], selectedImageIndex: number) => void
}

/**
 * 写真撮影セル。サムネイルを並べ、タップで全画面プレビュー（goPreviewPhoto）を開く。
 *
 * 同一 uuid の写真が重複登録されることがあるため、uuid で重複を除外する。
 * url が欠けていてもインデックスがずれないよう、空文字で補って並びを保つ。
 */
export const TableSectionPhotoCell: FC<Props> = ({
  images,
  recordedAt,
  goPreviewPhoto,
}) => {
  const uniquePhotos = useMemo(
    () =>
      images.filter(
        (image, index) =>
          images.findIndex(other => other.uuid === image.uuid) === index,
      ),
    [images],
  )
  const photoUrls = useMemo(
    () => uniquePhotos.map(photo => photo.url ?? ''),
    [uniquePhotos],
  )

  return (
    <TableCell sx={{ ...bodyCellSx, p: 0.5 }}>
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        gap={0.5}
        // 1枚のときは中央、複数枚は左揃え（奇数枚の最後の1枚が中央に浮かないように）
        justifyContent={uniquePhotos.length === 1 ? 'center' : 'flex-start'}
      >
        {uniquePhotos.map((photo, index) => (
          <Box
            key={photo.uuid ?? index}
            onClick={() => goPreviewPhoto(photoUrls, index)}
            sx={{
              position: 'relative',
              height: 34,
              width: 50,
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover .table-section-photo-overlay': { opacity: 1 },
            }}
          >
            <Box
              component="img"
              alt={photo.name ?? '写真'}
              src={photo.url ?? ''}
              sx={{ height: 34, width: 'auto', display: 'block', mx: 'auto' }}
            />
            <Box
              className="table-section-photo-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // 写真の上に重ねるスクリム。半透明の黒で D帳票レイアウトの表と見た目を揃える。
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: white,
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
          </Box>
        ))}
      </Stack>
      <TableSectionRecordedAt recordedAt={recordedAt} />
    </TableCell>
  )
}
